/**
 * Form Layout Component
 * =====================
 * 
 * UI layout component that provides step navigation, progress tracking, and form structure
 * for multi-step forms. This component works in conjunction with BaseFormComponent to
 * create a complete form submission experience.
 * 
 * Purpose:
 * - Renders the visual step navigation (stepper, progress bar, navigation buttons)
 * - Handles step navigation UI interactions (next, previous, direct step selection)
 * - Displays form progress and completion status
 * - Provides responsive layout for form content
 * 
 * Relationship with BaseFormComponent:
 * - This component is used as a CHILD component within individual form templates
 * - It receives all step-related data and callbacks from the parent form component
 * - The parent form component (extending BaseFormComponent) manages the actual form logic
 * - This component only handles the UI presentation and navigation interactions
 * 
 * Usage Pattern:
 * 1. Individual form components extend BaseFormComponent for form logic
 * 2. Form templates include this component for step navigation UI
 * 3. Parent form passes step data and navigation callbacks as inputs
 * 4. This component renders the navigation interface and calls parent methods
 * 
 * Key Features:
 * - Step-by-step navigation with progress tracking
 * - Responsive design for mobile and desktop
 * - Visual step completion indicators
 * - Smooth scrolling between steps
 * - Loading state integration
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, ViewChild, computed } from '@angular/core';
import { FormService } from '../../../services/form.service';
import { ScreenSizeService } from '../../../services/screen-size.service';
import { FormGroup } from '@angular/forms';
import { AttachmentUploadService } from '../../../services/attachment-upload.service';
import { IonContent } from '@ionic/angular';

/**
 * Interface defining the structure of a form step.
 * Each step represents a logical section of the form with its own validation requirements.
 */
export interface FormStep {
  /** Unique identifier for the step */
  id: string;
  /** Full title displayed in step headers and navigation */
  title: string;
  /** Shorter title for compact display (icons, stepper) */
  shortTitle: string;
  /** Ionic icon name for visual representation */
  icon: string;
  /** Optional description for additional context */
  description?: string;
}

@Component({
  selector: 'app-form-layout', 
  templateUrl: './form-layout.component.html',
  styleUrls: ['./form-layout.component.css']
})
export class FormLayoutComponent {
  /** Reference to the main content area for scrolling operations */
  @ViewChild(IonContent, { static: false }) content!: IonContent;

  // ============================================================================
  // FORM IDENTIFICATION INPUTS
  // ============================================================================
  
  /** Form title displayed in the header */
  @Input() title: string = '';
  /** Form icon displayed in the header */
  @Input() icon: string = ''; 
  /** Whether the form is disabled (prevents interactions) */
  @Input() disabled: boolean = false;
  /** Callback function for form submission */
  @Input() onSubmit!: () => void;

  // ============================================================================
  // STEP NAVIGATION INPUTS
  // ============================================================================
  // These inputs are provided by the parent form component (BaseFormComponent)
  // to control the step navigation behavior and display
  
  /** Array of form steps with their metadata */
  @Input() steps: FormStep[] = [];
  /** Currently active step identifier */
  @Input() currentStep: string = '';
  /** Function to check if navigation to a specific step is allowed */
  @Input() canNavigateToStep!: (stepId: string) => boolean;
  /** Callback for when user selects a different step */
  @Input() onStepChange!: (stepId: string) => void;
  /** Callback for navigation to previous step */
  @Input() onPreviousStep!: () => void;
  /** Callback for navigation to next step */
  @Input() onNextStep!: () => void;
  /** Function to calculate current progress percentage */
  @Input() getProgressPercentage!: () => number;
  /** Set of completed step identifiers for progress display */
  @Input() completedSteps: Set<string> = new Set();

  // ============================================================================
  // COMPUTED SIGNALS
  // ============================================================================
  // These signals provide reactive state for the template
  
  /** Computed signal for form loading state */
  readonly isLoading = computed(() => this.formService.dataLoading().isLoading);
  /** Computed signal for loading message display */
  readonly loadingMessage = computed(() => this.formService.dataLoading().message);
  /** Computed signal for mobile device detection */
  readonly isMobile = computed(() => this.screenSizeService.isMobileDevice());

  constructor(
    public formService: FormService, 
    protected attachmentService: AttachmentUploadService, 
    private screenSizeService: ScreenSizeService
  ) {
    this.formService.resetDataLoading();
  }

  // ============================================================================
  // STEP NAVIGATION METHODS
  // ============================================================================
  // These methods handle user interactions and delegate to parent component callbacks
  
  /**
   * Handles navigation to a specific step.
   * Delegates to parent component's onStepChange callback if navigation is allowed.
   * 
   * @param stepId - The target step identifier
   */
  goToStep(stepId: string): void {
    if (this.onStepChange && this.canNavigateToStep?.(stepId)) {
      this.onStepChange(stepId);
    }
  }

  /**
   * Handles navigation to the next step.
   * Scrolls to top of content area for better UX, then calls parent callback.
   */
  nextStep(): void {
    if (this.onNextStep) {
      this.content.scrollToTop(300); // 300ms animation duration
      this.onNextStep();
    }
  }

  /**
   * Handles navigation to the previous step.
   * Scrolls to top of content area for better UX, then calls parent callback.
   */
  previousStep(): void {
    if (this.onPreviousStep) {
      this.content.scrollToTop(300); // 300ms animation duration
      this.onPreviousStep();
    }
  }

  // ============================================================================
  // STEP STATE METHODS
  // ============================================================================
  // These methods provide step status information for the template
  
  /**
   * Checks if a specific step is currently active.
   * Used for highlighting the current step in the navigation UI.
   * 
   * @param stepId - The step identifier to check
   * @returns true if the step is currently active
   */
  isStepActive(stepId: string): boolean {
    return this.currentStep === stepId;
  }

  /**
   * Checks if a step has been completed.
   * Used for progress indicators and step completion styling.
   * 
   * @param stepId - The step identifier to check
   * @returns true if the step has been completed
   */
  isStepCompleted(stepId: string): boolean {
    return this.completedSteps.has(stepId);
  }

  /**
   * Checks if the current step is the first step.
   * Used to conditionally show/hide previous button.
   * 
   * @returns true if current step is the first step
   */
  isFirstStep(): boolean {
    return this.steps.length > 0 && this.currentStep === this.steps[0].id;
  }

  /**
   * Checks if the current step is the last step.
   * Used to conditionally show/hide next button and show submit button.
   * 
   * @returns true if current step is the last step
   */
  isLastStep(): boolean {
    return this.steps.length > 0 && this.currentStep === this.steps[this.steps.length - 1].id;
  }

  /**
   * Gets the current step number (1-based) for display purposes.
   * Used in progress indicators and step counters.
   * 
   * @returns The current step number (1, 2, 3, etc.)
   */
  getCurrentStepNumber(): number {
    const index = this.steps.findIndex(step => step.id === this.currentStep);
    return index >= 0 ? index + 1 : 1;
  }

  /**
   * Gets the total number of steps for progress calculations.
   * Used in progress bars and step counters.
   * 
   * @returns The total number of steps in the form
   */
  getTotalSteps(): number {
    return this.steps.length;
  }

  /**
   * Determines if the form is a single-step form. Used to suppress step navigation UI.
   * @returns true when only one step is defined
   */
  isSingleStep(): boolean {
    return this.steps && this.steps.length === 1;
  }
}